import React, { createContext, useContext } from 'react';

import { AlertTriangle, Check, Mail } from 'lucide-react';

import { formatCurrency } from '../../utils/utils';

import type { DocumentCheck, LineItemsListResponse } from '../../api';

/** ROC-105: figures a person confirms before the quote goes out (v9 st-hearth / s-analysis). */
export interface LineReview {
	/** Flagged line id → confidence (0-1). */
	flagged: Map<string, number>;
	/** Low-confidence lines a person already confirmed. */
	confirmed: Set<string>;
	/** Line id → statement page it was read from, when the platform sends it. */
	pages: Map<string, number>;
	/** Partly readable statement with no line confidence: one check for the whole statement. */
	statementNeedsCheck: boolean;
	statementChecked: boolean;
	/** Customize Quote and the discount slider are locked while this is true. */
	blocked: boolean;
	printedTotal: number | null;
	printedPage: number | null;
	readTotal: number | null;
}

const REVIEW_THRESHOLD = 0.8;

export const lineReviewFrom = (data?: LineItemsListResponse): LineReview => {
	const flagged = new Map<string, number>();
	const confirmed = new Set<string>();
	const pages = new Map<string, number>();
	for (const item of data?.line_items ?? []) {
		const score = item.confidence_score;
		const page = item.extra_attributes?.page_number;
		if (typeof page === 'number') pages.set(item.id, page);
		if (score == null) continue;
		if (item.needs_review) flagged.set(item.id, score);
		else if (score < REVIEW_THRESHOLD && item.extra_attributes?.confirmed)
			confirmed.add(item.id);
	}
	const statementNeedsCheck = Boolean(data?.statement_needs_check);
	const totals = data?.calculated_totals;
	return {
		flagged,
		confirmed,
		pages,
		statementNeedsCheck,
		statementChecked: Boolean(data?.figures_checked),
		blocked: flagged.size > 0 || statementNeedsCheck,
		printedTotal: totals?.document_total?.amount ?? null,
		printedPage: totals?.document_total?.page_number ?? null,
		readTotal: totals?.calculated_total ?? null,
	};
};

/** The one page the flagged lines come from, if they share it. */
const flaggedPage = (review: LineReview): number | null => {
	const found = new Set(
		[...review.flagged.keys()].map((id) => review.pages.get(id)),
	);
	const [only] = [...found];
	return found.size === 1 && typeof only === 'number' ? only : null;
};

const fromPage = (page: number | null) => (page ? ` from page ${page}` : '');

/** Customize Quote / slider tooltip, as v9: "Confirm the figures from page 4 first". */
export const quoteLockedMessage = (review: LineReview) =>
	`Confirm the figures${fromPage(flaggedPage(review))} first`;

const percent = (score: number) => `${Math.round(score * 100)}%`;
const WORDS = [
	'',
	'one',
	'two',
	'three',
	'four',
	'five',
	'six',
	'seven',
	'eight',
	'nine',
	'ten',
];
const word = (n: number) => WORDS[n] ?? String(n);
const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 's'}`;

export const isPartlyReadable = (check?: DocumentCheck | null) =>
	check?.status === 'flagged' &&
	check.issues.length > 0 &&
	check.issues.every((issue) => issue.code === 'PARTLY_READABLE');

interface LineReviewContextValue {
	review: LineReview;
	onConfirm: (lineItemIds?: string[]) => void;
	isConfirming: boolean;
}

const LineReviewContext = createContext<LineReviewContextValue | null>(null);

export const LineReviewProvider = LineReviewContext.Provider;

// v9 .btn .btn-sm
const BTN_SM =
	'inline-flex items-center justify-center gap-2 whitespace-nowrap px-3 py-1 text-xs leading-[18px] font-medium rounded-md border transition-colors disabled:opacity-50 [&_svg]:w-3 [&_svg]:h-3';
const BTN_PRIMARY_SM = `${BTN_SM} bg-primary-500 hover:bg-primary-600 text-white border-transparent`;
const BTN_SECONDARY_SM = `${BTN_SM} bg-white hover:bg-gray-50 text-primary-700 border-gray-300`;
// v9 .readtag
const READTAG =
	'inline-flex items-center gap-1 whitespace-nowrap ml-2 px-2 py-px rounded-full border text-[11px] font-semibold normal-case tracking-normal';

/** v9 ".readtag" "Read from page 4 · check" + Confirm on a flagged line; "Confirmed" after. */
export const LineReviewBadge: React.FC<{ lineItemId: string }> = ({
	lineItemId,
}) => {
	const ctx = useContext(LineReviewContext);
	if (!ctx) return null;
	const { review } = ctx;
	if (review.confirmed.has(lineItemId)) {
		return (
			<span
				className={`${READTAG} text-[#416211] bg-[#e4f7c2] border-[#d1f19b]`}
			>
				Confirmed
			</span>
		);
	}
	const score = review.flagged.get(lineItemId);
	if (score === undefined) return null;
	const page = review.pages.get(lineItemId);
	return (
		<>
			<span
				className={`${READTAG} text-[#b06a0b] bg-[#fff0dc] border-[#ffd59e]`}
			>
				{page ? `Read from page ${page}` : percent(score)} · check
			</span>
			<button
				type="button"
				className={`${BTN_SECONDARY_SM} ml-2 px-2.5 py-0.5 flex-shrink-0`}
				disabled={ctx.isConfirming}
				onClick={() => ctx.onConfirm([lineItemId])}
			>
				Confirm
			</button>
		</>
	);
};

/** Flagged line ids, for the v9 ".fee tr.chk-row td.lead" background. */
export const useFlaggedLines = (): ReadonlyMap<string, number> | undefined =>
	useContext(LineReviewContext)?.review.flagged;

export const FLAGGED_ROW_CLASS = '!bg-[#fffbf3]';

/** v9 Fee Analysis head: "2 figures to check" pill, or the usual badge. */
export const FeeAnalysisReviewPill: React.FC<{ fallback: React.ReactNode }> = ({
	fallback,
}) => {
	const ctx = useContext(LineReviewContext);
	if (!ctx?.review.blocked) return <>{fallback}</>;
	const left = ctx.review.flagged.size;
	return (
		<span className="inline-flex items-center gap-1.5 whitespace-nowrap px-2 py-1 rounded-full border text-xs font-medium leading-[1.3] bg-sand-100 text-sand-900 border-sand-300">
			{left > 0 ? `${plural(left, 'figure')} to check` : 'Figures to check'}
		</span>
	);
};

/** v9 Fee Analysis head: "Confirm all". */
export const FeeAnalysisConfirmAll: React.FC = () => {
	const ctx = useContext(LineReviewContext);
	if (!ctx?.review.blocked) return null;
	return (
		<button
			type="button"
			className={BTN_SECONDARY_SM}
			disabled={ctx.isConfirming}
			onClick={() => ctx.onConfirm()}
		>
			Confirm all
		</button>
	);
};

export const FEE_ANALYSIS_ID = 'ckFee';

const scrollToFees = () =>
	document
		.getElementById(FEE_ANALYSIS_ID)
		?.scrollIntoView({ behavior: 'smooth', block: 'start' });

interface PartlyReadableBannerProps {
	review: LineReview;
	check: DocumentCheck;
}

/** v9 ".cbanner.soft": "Part of this statement is hard to read", or ".cbanner.ok" once confirmed. */
export const PartlyReadableBanner: React.FC<PartlyReadableBannerProps> = ({
	review,
	check,
}) => {
	const issue = check.issues[0];
	if (!review.blocked) {
		if (!review.statementChecked && review.confirmed.size === 0) return null;
		return (
			<div className="flex gap-4 items-start px-6 py-5 border-b bg-[#f5fbe9] border-[#d1f19b] text-[#416211]">
				<span className="w-10 h-10 rounded-full flex items-center justify-center flex-none bg-[#e4f7c2]">
					<Check className="w-5 h-5" />
				</span>
				<div>
					<div className="text-base font-semibold">Figures confirmed</div>
					<div className="text-sm mt-1 max-w-[80ch]">
						The figures were confirmed. The quote is ready.
					</div>
				</div>
			</div>
		);
	}
	const mail = `mailto:?subject=${encodeURIComponent('Your statement')}&body=${encodeURIComponent(issue?.email_message ?? '')}`;
	return (
		<div className="flex gap-4 items-start px-6 py-5 border-b bg-[#fffbf3] border-[#ffe6c2] text-[#6e3f04]">
			<span className="w-10 h-10 rounded-full flex items-center justify-center flex-none bg-[#fff0dc] text-[#b06a0b]">
				<AlertTriangle className="w-5 h-5" />
			</span>
			<div>
				<div className="text-base font-semibold">
					Part of this statement is hard to read
				</div>
				<div className="text-sm mt-1 max-w-[80ch]">{issue?.reason}</div>
				<div className="flex gap-2 flex-wrap mt-3.5">
					<button
						type="button"
						className={BTN_PRIMARY_SM}
						onClick={scrollToFees}
					>
						Check the figures
					</button>
					<a className={BTN_SECONDARY_SM} href={mail}>
						<Mail />
						Ask for a clearer copy
					</a>
				</div>
			</div>
		</div>
	);
};

interface PersonReviewBannerProps {
	review: LineReview;
	onShowLog: () => void;
	onConfirmAll: () => void;
	isConfirming: boolean;
}

/** v9 s-analysis sand ".status-row" "Person review required" (line confidence, readable file). */
export const PersonReviewBanner: React.FC<PersonReviewBannerProps> = ({
	review,
	onShowLog,
	onConfirmAll,
	isConfirming,
}) => {
	const n = review.flagged.size;
	if (n === 0) return null;
	const lowest = Math.min(...review.flagged.values());
	const count = word(n);
	return (
		<div className="flex items-center justify-between gap-4 px-6 py-4 border-b bg-sand-50 border-sand-300 flex-wrap">
			<div className="flex items-center gap-4">
				<span className="w-9 h-9 rounded-full flex items-center justify-center flex-none bg-sand-300 text-sand-900">
					<AlertTriangle className="w-5 h-5" />
				</span>
				<div>
					<div className="text-base font-medium text-gray-900">
						Person review required
					</div>
					<div className="text-sm text-gray-500">
						{`${count[0].toUpperCase()}${count.slice(1)} ${n === 1 ? 'line was' : 'lines were'} read at ${percent(lowest)} confidence. Their values are shown and flagged below. Confirm them before the quote goes out.`}
					</div>
				</div>
			</div>
			<div className="flex items-center gap-4">
				<button type="button" className={BTN_SECONDARY_SM} onClick={onShowLog}>
					Show the log
				</button>
				<button
					type="button"
					className={BTN_PRIMARY_SM}
					disabled={isConfirming}
					onClick={onConfirmAll}
				>
					{n === 1 ? 'Confirm the line' : `Confirm the ${count} lines`}
				</button>
			</div>
		</div>
	);
};

const DOT: Record<'ok' | 'warn', string> = {
	ok: 'bg-[#a2e63c]',
	warn: 'bg-[#ff9d1c]',
};

/** v9 "Review checklist" card: printed total vs figures read, figures to confirm. */
export const ReviewChecklist: React.FC<{ review: LineReview }> = ({
	review,
}) => {
	const { printedTotal, readTotal } = review;
	const items: { tone: 'ok' | 'warn'; body: string; view?: boolean }[] = [];
	if (printedTotal != null && readTotal != null) {
		const where = review.printedPage
			? `Page ${review.printedPage}`
			: 'The statement';
		items.push(
			Math.abs(printedTotal - readTotal) < 0.005
				? {
						tone: 'ok',
						body: `Printed total ties: ${formatCurrency(printedTotal)}`,
					}
				: {
						tone: 'warn',
						body: `The figures read add up to ${formatCurrency(readTotal)}. ${where} prints ${formatCurrency(printedTotal)}.`,
					},
		);
	}
	const page = fromPage(flaggedPage(review));
	if (review.blocked) {
		const left = review.flagged.size;
		items.push({
			tone: 'warn',
			body:
				left > 0
					? `${plural(left, 'figure')}${page} to confirm`
					: 'Figures to confirm',
			view: true,
		});
	} else if (review.confirmed.size > 0 || review.statementChecked) {
		items.push({ tone: 'ok', body: `Figures${page} confirmed` });
	}
	if (items.length === 0) return null;
	return (
		<div className="bg-white border border-gray-200 rounded-lg">
			<div className="px-6 pt-6">
				<div className="text-lg font-normal leading-normal text-gray-900">
					Review checklist
				</div>
			</div>
			<div className="p-6">
				<ul className="list-none m-0 p-0">
					{items.map((item, index) => (
						<li
							key={item.body}
							className={`flex gap-3 items-start text-sm ${index ? 'py-2.5 border-t border-gray-100' : 'pb-2.5'}`}
						>
							<span
								className={`w-2 h-2 rounded-full mt-[7px] flex-none ${DOT[item.tone]}`}
							/>
							<div className="flex-1 min-w-0">{item.body}</div>
							{item.view && (
								<button
									type="button"
									className="ml-auto text-xs text-gray-500 whitespace-nowrap"
									onClick={scrollToFees}
								>
									view
								</button>
							)}
						</li>
					))}
				</ul>
			</div>
		</div>
	);
};
